import * as THREE from "three/webgpu";
import type { SurfaceFootprintDraftState } from "../../app/storeTypes.ts";
import {
  basisUV,
  cubeTransformFromDraft,
  uvToWorld,
} from "../../modeling/surfaceBoxMath.ts";
import {
  linearRampProfileFromEllipseEdge,
  type SurfaceProfileTarget,
  type SurfaceVec2,
  sampleSurfaceEllipseLoop,
  sampleSurfaceRectangleLoop,
  surfaceProfileFromTarget,
  surfaceProfileHeightT,
} from "../../modeling/surfaceProfiles.ts";

export interface SurfaceProfilePreview {
  anchor: [number, number, number];
  wirePositions: Float32Array;
}

export function buildSurfaceFootprintProfilePreview(
  draft: SurfaceFootprintDraftState,
  kind: "box" | "cylinder",
): SurfaceProfilePreview {
  const t = cubeTransformFromDraft(draft);
  const baseCenter: [number, number, number] = [
    t.position[0] - (draft.height / 2) * draft.basis.normal[0],
    t.position[1] - (draft.height / 2) * draft.basis.normal[1],
    t.position[2] - (draft.height / 2) * draft.basis.normal[2],
  ];
  const width = Math.abs(t.width);
  const depth = Math.abs(t.depth);
  const loop =
    kind === "box"
      ? sampleSurfaceRectangleLoop(width, depth)
      : sampleSurfaceEllipseLoop(width / 2, depth / 2, 32);
  const localTarget =
    draft.profileTarget?.kind === "edge"
      ? ({ kind: "edge", edgeIndex: draft.profileTarget.edgeIndex } as const)
      : ({ kind: "face" } as const);
  const profile =
    kind === "cylinder" && localTarget.kind === "edge"
      ? linearRampProfileFromEllipseEdge(
          width / 2,
          depth / 2,
          32,
          localTarget.edgeIndex,
        )
      : surfaceProfileFromTarget(loop, localTarget);
  const baseUv = basisUV(draft.basis, baseCenter);
  const worldLoop = loop.map(([x, y]) =>
    uvToWorld(draft.basis, baseUv.u + x, baseUv.v + y),
  );

  if (profile.kind !== "linearRamp") {
    return {
      anchor: baseCenter,
      wirePositions: buildConstantPrismWire(
        worldLoop,
        draft.basis.normal,
        draft.height,
      ),
    };
  }

  const top = worldLoop.map((p, i) => {
    const [x, y] = loop[i];
    const h = draft.height * surfaceProfileHeightT(profile, x, y);
    return offsetWorld(p, draft.basis.normal, h);
  });
  const selectedEdge =
    draft.profileTarget?.kind === "edge" ? draft.profileTarget.edgeIndex : 0;
  const edgeIndex = positiveModulo(selectedEdge, loop.length);
  const anchorLocal = edgeMidpoint(loop, edgeIndex);
  const anchorT = surfaceProfileHeightT(
    profile,
    anchorLocal[0],
    anchorLocal[1],
  );
  const anchorBase = uvToWorld(
    draft.basis,
    baseUv.u + anchorLocal[0],
    baseUv.v + anchorLocal[1],
  );

  return {
    anchor: offsetWorld(anchorBase, draft.basis.normal, draft.height * anchorT),
    wirePositions: buildVariablePrismWire(worldLoop, top),
  };
}

export function buildSurfacePolyProfilePreview(
  vertices: ReadonlyArray<readonly [number, number, number]>,
  normal: readonly [number, number, number],
  height: number,
  target: SurfaceProfileTarget | undefined,
): SurfaceProfilePreview {
  const n = vertices.length;
  if (n < 3) {
    return { anchor: [0, 0, 0], wirePositions: new Float32Array() };
  }
  const centroid = averageWorld(vertices);
  const tangent = normalizeWorld([
    vertices[1][0] - vertices[0][0],
    vertices[1][1] - vertices[0][1],
    vertices[1][2] - vertices[0][2],
  ]);
  const normalVec = normalizeWorld([normal[0], normal[1], normal[2]]);
  const bitangent = normalizeWorld(cross(normalVec, tangent));
  const profileLoop: SurfaceVec2[] = vertices.map((v) => [
    dotWorld(subWorld(v, centroid), tangent),
    dotWorld(subWorld(v, centroid), bitangent),
  ]);
  const profile = surfaceProfileFromTarget(profileLoop, target);
  if (profile.kind !== "linearRamp") {
    return {
      anchor: centroid,
      wirePositions: buildConstantPrismWire(vertices, normalVec, height),
    };
  }

  const top = vertices.map((p, i) => {
    const [x, y] = profileLoop[i];
    return offsetWorld(
      p,
      normalVec,
      height * surfaceProfileHeightT(profile, x, y),
    );
  });
  const edgeIndex =
    target?.kind === "edge" ? positiveModulo(target.edgeIndex, n) : 0;
  const [a, b] = [vertices[edgeIndex], vertices[(edgeIndex + 1) % n]];
  const anchorLocal = edgeMidpoint(profileLoop, edgeIndex);
  const anchorBase: [number, number, number] = [
    (a[0] + b[0]) * 0.5,
    (a[1] + b[1]) * 0.5,
    (a[2] + b[2]) * 0.5,
  ];
  const anchorT = surfaceProfileHeightT(
    profile,
    anchorLocal[0],
    anchorLocal[1],
  );
  return {
    anchor: offsetWorld(anchorBase, normalVec, height * anchorT),
    wirePositions: buildVariablePrismWire(vertices, top),
  };
}

export function applyLinePositions(
  geometry: THREE.BufferGeometry,
  positions: Float32Array,
): void {
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
}

function buildConstantPrismWire(
  base: ReadonlyArray<readonly [number, number, number]>,
  normal: readonly [number, number, number],
  height: number,
): Float32Array {
  const top = base.map((p) => offsetWorld(p, normal, height));
  return buildVariablePrismWire(base, top);
}

function buildVariablePrismWire(
  base: ReadonlyArray<readonly [number, number, number]>,
  top: ReadonlyArray<readonly [number, number, number]>,
): Float32Array {
  const n = base.length;
  const positions = new Float32Array(3 * n * 6);
  let w = 0;
  const write = (
    a: readonly [number, number, number],
    b: readonly [number, number, number],
  ) => {
    positions[w++] = a[0];
    positions[w++] = a[1];
    positions[w++] = a[2];
    positions[w++] = b[0];
    positions[w++] = b[1];
    positions[w++] = b[2];
  };
  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    write(base[i], base[j]);
    write(top[i], top[j]);
    write(base[i], top[i]);
  }
  return positions;
}

function edgeMidpoint(
  loop: ReadonlyArray<readonly [number, number]>,
  edgeIndex: number,
): [number, number] {
  const i = positiveModulo(edgeIndex, loop.length);
  const a = loop[i];
  const b = loop[(i + 1) % loop.length];
  return [(a[0] + b[0]) * 0.5, (a[1] + b[1]) * 0.5];
}

function averageWorld(
  points: ReadonlyArray<readonly [number, number, number]>,
): [number, number, number] {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of points) {
    x += p[0];
    y += p[1];
    z += p[2];
  }
  return [x / points.length, y / points.length, z / points.length];
}

function offsetWorld(
  p: readonly [number, number, number],
  n: readonly [number, number, number],
  amount: number,
): [number, number, number] {
  return [p[0] + amount * n[0], p[1] + amount * n[1], p[2] + amount * n[2]];
}

function subWorld(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): [number, number, number] {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dotWorld(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): [number, number, number] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function normalizeWorld(
  v: readonly [number, number, number],
): [number, number, number] {
  const len = Math.hypot(v[0], v[1], v[2]);
  return len > 1e-9 ? [v[0] / len, v[1] / len, v[2] / len] : [1, 0, 0];
}

function positiveModulo(value: number, mod: number): number {
  return ((value % mod) + mod) % mod;
}
